import React, { useState } from "react";
import { Moon, Sun, HelpCircle } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Tooltip from "../common/Tooltip";
import PartnerLogos from "./PartnerLogos";
import { APP_CONFIG } from "../../utils/constants";

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);

  return (
    <>
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 py-4">
            {/* Left side - Help Button */}
            <div className="flex-1 flex items-center justify-start">
              <Tooltip
                content="Get help with booking your consultation"
                position="bottom"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHelp(true)}
                  className="p-2"
                  aria-label="Help"
                >
                  <HelpCircle className="w-5 h-5" />
                </Button>
              </Tooltip>
            </div>
            {/* Centered Logo */}
            <div className="flex justify-center px-8">
              <div className="py-3">
                <img
                  src="/logo.png"
                  alt="Eclipse Tint Logo"
                  className="h-16 w-auto filter invert dark:filter-none transition-all duration-200"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
            {/* Navigation and Actions */}
            <div className="flex-1 flex items-center justify-end space-x-2 sm:space-x-4">
              {/* Theme Toggle */}
              <Tooltip
                content={`Switch to ${
                  theme === "light" ? "dark" : "light"
                } mode`}
                position="bottom"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className="p-2"
                  aria-label={`Switch to ${
                    theme === "light" ? "dark" : "light"
                  } mode`}
                >
                  {theme === "light" ? (
                    <Moon className="w-5 h-5" />
                  ) : (
                    <Sun className="w-5 h-5" />
                  )}
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </header>

      {/* Partner Logos */}
      <PartnerLogos />

      {/* Help Modal */}
      <Modal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Need Help?"
        size="md"
      >
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              How to Book a Consultation
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
              <li>Fill in your contact details</li>
              <li>
                Select your main concern or pain point, and optionally enter
                your glass panel measurements
              </li>
              <li>Choose an available time slot</li>
              <li>Confirm your booking</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Measuring Your Glass
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Measure the length and width of each glass panel in millimeters.
              Don't worry about being exact - our sales consultant will take
              precise measurements during the consultation.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Need More Help?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              Call us at{" "}
              <a
                href={`tel:${APP_CONFIG.CONTACT_PHONE}`}
                className="text-amber-600 hover:text-amber-700 font-medium"
              >
                {APP_CONFIG.CONTACT_PHONE.replace(
                  /(\\d{4})(\\d{3})(\\d{3})/,
                  "$1 $2 $3"
                )}
              </a>{" "}
              or email{" "}
              <a
                href={`mailto:${APP_CONFIG.CONTACT_EMAIL}`}
                className="text-amber-600 hover:text-amber-700 font-medium break-all"
              >
                {APP_CONFIG.CONTACT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Header;
